import { TextBadge } from '@carvana/ds-badge';
import { ChatLogoCvna } from '@carvana/ds-icons';
import cx from 'classnames';
import React from 'react';
import { useBrandCapabilities } from '|/brand';
import Link from '|/components/carvana-ds/link';
import LinkButton from '|/components/carvana-ds/link-button';
import VehicleImage from '|/components/vehicle-image';
import { isNewVehicle } from '|/helpers';
import currency from '|/helpers/currency';
import { isCarvanaNativeApp } from '|/helpers/is-native-app';
import { MobileWebviewEvents } from '|/helpers/mobile-webview-events';
import sendNativeEvent from '|/helpers/send-native-event';

import { ReviewSummaryResponse } from '|/services/purchase-api';
import { PriceDetailsView } from '../derive-price-details';
import styles from '../price-details.module.css';

interface VehicleTileProps {
  summary: ReviewSummaryResponse;
  view: PriceDetailsView;
  isCheckoutPage: boolean;
  isMobileViewport: boolean;
  onSupportClick: () => void;
}

/**
 * Three header variants share one tile (review-order, checkout desktop,
 * checkout mobile). Checkout (both
 * viewports) adds the price/mileage line, View vehicle, and a larger/darker
 * title+trim; Support & Contact is checkout **mobile only** — the desktop frame
 * omits it entirely, matching control's own >=768px `.modalTitleMobile` hide.
 */
const VehicleTile = ({
  summary,
  view,
  isCheckoutPage,
  isMobileViewport,
  onSupportClick
}: VehicleTileProps) => {
  const { checkout } = useBrandCapabilities();
  const {
    vehicle: { odometer = 0, stockNumber = null, id: vehicleId = null } = {},
    totals: { vehicleStickerPrice = null } = {}
  } = summary;

  return (
    <div className={styles.vehicleTile}>
      {isCheckoutPage && isMobileViewport && (
        <LinkButton
          size="sm"
          underline={false}
          className={styles.supportLink}
          onClick={onSupportClick}
          data-analytics-id="pp_order_summary_support_contact"
          data-testid="pp_price-details_support-contact"
        >
          <ChatLogoCvna size={16} color="currentColor" aria-hidden /> Support &amp; Contact
        </LinkButton>
      )}

      <div className={styles.vehicleRow}>
        <VehicleImage
          className={styles.vehicleImage}
          stockNumber={stockNumber}
          width={172}
          alt=""
          data-testid="vehicle-image"
        />
        <div className={styles.vehicleInfo}>
          {checkout.sellsPreOwnedVehicles && isNewVehicle(summary.vehicle) && (
            <TextBadge customBgColor="var(--decorative-3-strong)" priority="custom">
              Brand new
            </TextBadge>
          )}
          <p className={cx(styles.vehicleTitle, isCheckoutPage && styles.vehicleTitleLg)}>
            {view.vehicleTitle}
          </p>
          {view.vehicleTrim && (
            <p className={cx(styles.vehicleTrim, isCheckoutPage && styles.vehicleTrimStrong)}>
              {view.vehicleTrim}
            </p>
          )}

          {isCheckoutPage && (
            <>
              <p className={styles.vehicleMeta}>
                {vehicleStickerPrice != null && (
                  <>
                    {currency(vehicleStickerPrice, {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0
                    })}
                    <span className={styles.metaDot} aria-hidden />
                  </>
                )}
                {(odometer ?? 0).toLocaleString('en-US')} miles
              </p>
              {vehicleId != null && (
                <Link
                  href={`/vehicle/${vehicleId}`}
                  variant="standalone"
                  size="sm"
                  onClick={(e: React.MouseEvent) => {
                    if (isCarvanaNativeApp()) {
                      e.preventDefault();
                      sendNativeEvent({
                        event: MobileWebviewEvents.CONTINUE_TO_VDP,
                        payload: { vehicleId, source: 'checkout_order_summary' },
                        source: 'purchase'
                      });
                    }
                  }}
                  data-analytics-id="order-summary-view-vehicle"
                >
                  View vehicle
                </Link>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VehicleTile;
